/* eslint-disable no-console */
import axios from 'axios';
import { isProd } from 'config/config';
import { cleanStatuses, formatFormData, getWarehouseTimezoneOffSet } from 'helpers/utils';
import buildQuery from 'odata-query';

const HOST = isProd ? '/' : '/';
const isProdDomain = window.location.hostname === 'www.powerdistributors.com';

if (!isProd) {
    axios.defaults.headers.common[process.env.LOGIN_PROP || ''] = process.env.LOGIN_TOKEN || '';
}

axios.defaults.headers.common['Cache-Control'] = 'no-cache';
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.common.timezoneOffset = new Date().getTimezoneOffset();

// Function to wait for an element using MutationObserver
function waitForElm(selector) {
    return new Promise((resolve) => {
        const el = document.querySelector(selector);
        if (el) return resolve(el);

        new MutationObserver((_, observer) => {
            const el = document.querySelector(selector);
            if (el) {
                resolve(el);
                observer.disconnect();
            }
        }).observe(document.body, { childList: true, subtree: true });
    });
}

export const debounce = function debounce(fn, delay) {
    var timeoutID = null;
    return function () {
        clearTimeout(timeoutID);
        var args = arguments;
        var that = this;
        timeoutID = setTimeout(function () {
            fn.apply(that, args);
        }, delay);
    };
};

export const API = {
    get: async (endpoint) => {
        const res = await axios.get(`${HOST}${endpoint}`).catch((err) => {
            return err;
        });
        if (res.status !== 200) {
            console.log(`Object at: ${endpoint}`, res);
            return {
                notify: true,
                error: res,
            };
        }
        return res.data;
    },
    address: {
        get: async (id) => {
            const res = await axios.get(`${HOST}customapi/Address/GetAddress?AddressId=${id}`).catch((err) => err);
            if (res.status !== 200) {
                console.error(`Get Address Failed`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        shippingVia: async (isCommercial) => {
            const res = await axios.post(`${HOST}customapi/Address/ShippingVia?isCommercial=${isCommercial}`).catch((err) => err);
            if (res.status !== 200) {
                console.error(`Get ShipVias Failed`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
    },
    cart: {
        add: async (items = {}) => {
            const res = await axios.post(`${HOST}customapi/Cart/Add`, items).catch((err) => err);
            if (res.status !== 200) {
                console.error(`Add to Cart Failed`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        delete: async (items = [], caller = 'unknown') => {
            var origin = '(delete): ' + caller;

            const res = await axios
                .delete(`${HOST}customapi/Cart/Delete`, {
                    data: { items, origin },
                })
                .catch((err) => err);
            if (res.status !== 200) {
                console.error(`Cart Delete Error`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        emptyCart: async (cartType = 'regular', clearActive = false) => {
            const res = await axios.delete(`${HOST}customapi/Cart/EmptyCart?cartType=${cartType}?clearActive=${clearActive}`).catch((err) => err);
            if (res.status !== 200) {
                console.error(`Empty Cart Error`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        getInactive: async (cartType = 'regular') => {
            const response = await axios.get(`${HOST}customapi/Cart/GetInactive?cartType=${cartType}&includeAlternateWarehouseCheck=true`).catch((err) => err);

            if (response.status !== 200) {
                console.error(`Client Service Error`, response);
                return {
                    notify: true,
                    error: response,
                };
            }

            // Check if the response includes the 'isAlternateWarehouseDisabled' property
            if (response.data.hasOwnProperty('isAlternateWarehouseDisabled')) {
                const { retVal, isAlternateWarehouseDisabled, alternativeParts } = response.data;
                return { retVal, isAlternateWarehouseDisabled, alternativeParts };
            }

            // If 'isAlternateWarehouseDisabled' is not in the response, just return 'retVal'
            return response.data;
        },
        updateQuantity: async (cartItemId, qty, note) => {
            try {
                const res = await axios.put(`${HOST}customapi/Cart/UpdateQuantity`, {
                    cartItemId,
                    qty,
                    note,
                });
                console.log(res.data);
                return res.data;
            } catch (error) {
                console.error(`Cart/UpdateQuantity Failed`, error.response);
                let errorMessage = 'An unknown error occurred';
                if (error.response && error.response.data && error.response.data.Message) {
                    errorMessage = error.response.data.Message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                swal({
                    title: 'Maximum quantity exceeded',
                    text: errorMessage,
                    icon: 'error',
                }).then(() => {
                    location.reload();
                });

                return {
                    notify: true,
                    error: errorMessage,
                };
            }
        },
    },
    clientservice: {
        get: async (endpoint) => {
            const res = await axios.get(`${HOST}/api/pdclientservice/${endpoint}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.error(`Client Service Error`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        getResource: async (collection, query) => {
            const res = await axios.get(`${HOST}/api/pdclientservice/${collection}?${query}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.error(`Client Service Error`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        getDocuments: async (query, pagination = null) => {
            query = buildQuery(query);
            const res = await axios.get(`/api/pdclientservice/documents${query}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.error(`Client Service Error - Documents`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            if (pagination) {
                pagination.items.total = res.data['@odata.count'];
                pagination.pages.total = Math.ceil(res.data['@odata.count'] / pagination.items.view);
                pagination.items.current =
                    pagination.pages.current === pagination.pages.total
                        ? Math.abs(pagination.items.total - pagination.items.view * (pagination.pages.current - 1))
                        : pagination.items.view;
                pagination.pages.collection = [];
                for (let i = 1; i <= pagination.pages.total; i++) {
                    pagination.pages.collection.push({
                        number: i,
                        active: i === pagination.pages.current,
                    });
                }
                return {
                    data: res.data.value,
                    pagination,
                };
            }
            return {
                data: res.data.value,
            };
        },
    },
    crossref: {
        autocomplete: async (search, take = 10) => {
            const res = await axios.get(`${HOST}customapi/CrossRef/Autocomplete?search=${search}&take=${take}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        detail: async (id, cartType = 'Regular') => {
            const res = await axios.get(`${HOST}customapi/CrossRef/Detail/${id}?cartType=${cartType}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                // console.log(`Detail query: ${id}`, res)
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
    },
    invoices: {
        history: {
            get: async (startDate, endDate, maxOrders) => {
                const query = [];

                if (startDate) {
                    query.push('StartDate=' + startDate);
                }

                if (endDate) {
                    query.push('EndDate=' + endDate);
                }

                if (maxOrders) {
                    query.push('ResultCount=' + maxOrders);
                }

                let queryString = '?';

                for (let i = 0; i < query.length; i++) {
                    if (i !== 0) {
                        queryString += '&';
                    }
                    queryString += query[i];
                }

                const res = await axios.get(`${HOST}customapi/Order/SearchInvoices${query.length ? queryString : ''}`).catch((err) => {
                    return err;
                });
                if (res.status !== 200) {
                    console.log(`Detail query: ${id}`, res);
                    return {
                        notify: true,
                        error: res,
                    };
                }
                return res.data;
            },
        },
    },
    orders: {
        history: {
            get: async (startDate, endDate, maxOrders) => {
                const query = [];

                if (startDate) {
                    query.push('StartDate=' + startDate);
                }

                if (endDate) {
                    query.push('EndDate=' + endDate);
                }

                if (maxOrders) {
                    query.push('ResultCount=' + maxOrders);
                }

                let queryString = '?';

                for (let i = 0; i < query.length; i++) {
                    if (i !== 0) {
                        queryString += '&';
                    }
                    queryString += query[i];
                }

                const res = await axios.get(`${HOST}customapi/Order/Search${query.length ? queryString : ''}`).catch((err) => {
                    return err;
                });
                if (res.status !== 200) {
                    console.log(`Detail query: ${id}`, res);
                    return {
                        notify: true,
                        error: res,
                    };
                }
                return res.data;
            },
        },
    },
    product: {
        autocomplete: async (search, manCodes = []) => {
            const config = {
                method: 'get',
                url: `${HOST}customapi/Product/Autocomplete`,
                params: {
                    search,
                    manCodes,
                },
            };
            const res = await axios(config).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(`Search query: ${search}`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        detail: async (id, promo, cartType = 'regular') => {
            const res = await axios.get(`${HOST}customapi/Product/Detail/${id}?cartType=${cartType}${promo ? `?promo=${promo}` : ``}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(`Part# ${id} doesn't exist`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        get: async (id, qty, cartType = 'regular', partNote = null, locationId = 0, includeAlternativePart = false) => {
            const res = await axios
                .get(
                    `${HOST}customapi/Product/Get/${id}?qty=${qty}&cartType=${cartType}&partNote=${partNote}&locationId=${locationId}&includeAlternativePart=${includeAlternativePart}`,
                )
                .catch((err) => err);
            if (res.status !== 200) {
                console.log(`Product# ${id} threw an error`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        getItemsCount: async (cartType = 'regular') => {
            const res = await axios.get(`${HOST}customapi/Product/GetItemsCount/?cartType=${cartType}`).catch((err) => err);
            if (res.status !== 200) {
                console.log(`GetItemsCount threw an error`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
    },
    warranty: {
        getEmptyJson: async (manCode) => {
            const res = await axios.get(`${HOST}customapi/Warranty/getEmptyJson?Manufacturer=${manCode}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(`Manufacturer: ${manCode}`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },

        getWarranty: async (warrantyId) => {
            if (warrantyId >= 300000) {
                // Redirect to new form
                window.location.href = '/warranty-form/?code=BS&id=' + warrantyId;
                return;
            }

            const res = await axios.get(`${HOST}customapi/Warranty/Get/${warrantyId}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(`Warranty ID: ${warrantyId}`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },

        getEligibleManufacturers: async () => {
            const res = await axios.get(`${HOST}customapi/Warranty/GetEligibleManufacturers`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log('Warranty/GetEligibleManufacturers', res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },

        getModels: async (manufacturerId, ProductCategory) => {
            const res = await axios
                .get(`${HOST}customapi/Warranty/GetModels?manufacturerId=${manufacturerId}&ProductCategory=${ProductCategory}`)
                .catch((err) => {
                    return err;
                });
            if (res.status !== 200) {
                console.log(`Manufacturer ID: ${manufacturerId}`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },

        getMFGSetting: async (mfgId) => {
            const res = await axios.get(`${HOST}customapi/Warranty/GetMFGSettings?mfgId=${mfgId}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(`MFG: ${warrantyId}`, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },

        submit: async (warrantyObj) => {
            const res = await axios.post(`${HOST}customapi/Warranty/Submit`, warrantyObj).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(warrantyObj, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },

        getOptionFields: async () => {
            const res = await axios.get(`${HOST}customapi/Warranty/GetOptionFields`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },

        addNote: async (id, Note) => {
            const res = await axios.post(`${HOST}customapi/Warranty/AddNote/${id}?Note=${Note}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(Note, res);
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        addJob: async (warrantyId, modelId, modelJobId) => {
            const res = await axios
                .post(`${HOST}customapi/Warranty/AddJob?warrantyId=${warrantyId}&modelId=${modelId}&modelJobId=${modelJobId}`)
                .catch((err) => {
                    return err;
                });
            if (res.status !== 200) {
                console.log(
                    {
                        warrantyId,
                        modelId,
                        modelJobId,
                    },
                    res,
                );
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        addLaborPart: async (warrantyId, productId, quantity) => {
            const res = await axios
                .post(`${HOST}customapi/Warranty/AddLaborPart?warrantyId=${warrantyId}&productId=${productId}&quantity=${quantity}`)
                .catch((err) => {
                    return err;
                });
            if (res.status !== 200) {
                console.log(
                    {
                        warrantyId,
                        productId,
                        quantity,
                    },
                    res,
                );
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        updateLaborPartQuantity: async (warrantyId, partRecordId, newQuantity, newMinutes) => {
            const res = await axios
                .post(
                    `${HOST}customapi/Warranty/UpdateLaborPartQuantity?warrantyId=${warrantyId}&partRecordId=${partRecordId}&newQuantity=${newQuantity}&newMinutes=${newMinutes}`,
                )
                .catch((err) => {
                    return err;
                });
            if (res.status !== 200) {
                console.log(
                    {
                        warrantyId,
                        partRecordId,
                        newQuantity,
                        newMinutes,
                    },
                    res,
                );
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        removeLaborPart: async (warrantyId, partRecordId) => {
            const res = await axios.post(`${HOST}customapi/Warranty/RemoveLaborPart?warrantyId=${warrantyId}&partRecordId=${partRecordId}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(
                    {
                        warrantyId,
                        partRecordId,
                    },
                    res,
                );
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        removeJob: async (warrantyId, jobId) => {
            const res = await axios.post(`${HOST}customapi/Warranty/RemoveJob?warrantyId=${warrantyId}&jobId=${jobId}`).catch((err) => {
                return err;
            });
            if (res.status !== 200) {
                console.log(
                    {
                        warrantyId,
                        jobId,
                    },
                    res,
                );
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data;
        },
        addAttachment: async ({ id, description, image }) => {
            const data = new FormData();
            data.append('file', image, image.name);

            const res = await axios
                .post(`${HOST}customapi/Uploads/Warranty?warrantyId=${id}&description=${description}`, data, {
                    headers: {
                        accept: 'application/json',
                        'Accept-Language': 'en-US,en;q=0.8',
                        'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
                    },
                })
                .catch((err) => {
                    return err;
                }); // eslint-disable-line
            if (res.status !== 200) {
                return {
                    notify: true,
                    error: res,
                };
            }
            return res.data || {};
        },
        editAttachmentDescription: async (id, newText) => {
            try {
                const res = await axios.post(`${HOST}customapi/Warranty/EditAttachmentDescription?id=${id}&description=${newText}`).catch((err) => {
                    return err;
                });
                if (res.status !== 200) {
                    console.log('Error Editing Attachment Description', id, newText, res);
                    return {
                        notify: true,
                        error: res,
                    };
                }
                return res.data;
            } catch (err) {
                return { notify: true, error: err.response || err };
            }
        },
        removeAttachment: async (id) => axios.delete(`${HOST}customapi/Uploads/DeleteAttachment?attachmentId=${id}`).catch((err) => err.response),
    },
};

// ==================================================
// Old Code
// ==================================================

// ==================================================
// ADDRESS
// ==================================================

export async function getCustomerAddresses(type = 'None') {
    const { data } = await axios.get(`${HOST}customapi/Address/GetCustomerAddresses?addressType=${type}`).catch(() => {});

    return data || {};
}

export const getAddress = async (id) => {
    const { data } = await axios.get(`${HOST}customapi/Address/GetAddress?AddressId=${id}`).catch((err) => {
        console.log(err.response.data);
        console.log(err.response.status);
        return {
            status: err.response.status,
            message: err.response.data,
        };
    });

    return data || {};
};

export async function getShippingBilling() {
    const data = await axios.get(`${HOST}customapi/Address/ShippingBilling`).catch((err) => {
        console.log(err.response.data.Message);
        console.log(err.response.status);

        return {
            status: err.response.status,
            message: err.response.data.Message,
        };
    });

    if (data.status != 200) {
        return data;
    }

    const { User, CartItems } = await fetchCart();
    // const fetch = await fetchCart();
    // const items = fetch.cart.CartItems;
    // const user = fetch.cart.User;
    // console.log('Data', data);
    // console.log('User', items);
    // console.log('CartItems', user);
    // if (data.status != 200) {
    //   return;
    // }

    const defaultCustomerAddress = await getAddress(null);

    let currentSameAsDefault;

    if (data.data.CurrentShippingAddress.Id === null) {
        currentSameAsDefault = true;
    } else {
        currentSameAsDefault = false;
    }

    return data.data
        ? {
              ...data.data,
              status: data.status,
              CartItems,
              currentSameAsDefault,
              defaultCustomerAddress,
              account: {
                  BillToNumber: User.NetSuiteCustomer.BillToNumber,
                  CustomerName: User.NetSuiteCustomer.CustomerName,
              },
          }
        : {};
}

export async function postShippingBilling(data) {
    return axios.post(`${HOST}customapi/Address/ShippingBilling`, data).catch((err) => ({
        status: err.response.status,
        Message: err.response.data.Message,
    }));
}

export async function validateAddress(postData) {
    const res = await axios.post(`${HOST}customapi/Address/ValidateAddress`, postData).catch((error) => {
        console.log(error);
        return error;
    });

    const Addresses = res.data.Addresses
        ? res.data.Addresses.map(({ AddressKeyFormat: k }) => ({
              City: k.PoliticalDivision2,
              State: k.PoliticalDivision1,
              StreetLine1: typeof k.AddressLine === 'object' ? k.AddressLine[0] : k.AddressLine,
              StreetLine2: typeof k.AddressLine === 'object' ? k.AddressLine[1] : postData.StreetLine2,
              Zip: `${k.PostcodePrimaryLow}-${k.PostcodeExtendedLow}`,
          }))
        : [
              {
                  City: postData.City,
                  State: postData.State,
                  StreetLine1: postData.StreetLine1,
                  StreetLine2: postData.StreetLine2,
                  Zip: postData.Zip,
              },
          ];

    return {
        ShipVias: res.data.ShipVias,
        Addresses,
        IsDifferent: res.data.IsDifferent,
        IsFailed: res.data.IsFailed,
    };
}

// ==================================================
// CAREER
// ==================================================

export async function getCareers() {
    const { data, status } = await axios.get(`${HOST}customapi/Career/Get`).catch((err) => err.response);

    return {
        data,
        status,
    };
}

// ==================================================
// CART
// ==================================================

export async function fetchCart(includeall = false) {
    const { data } = await axios
        .get(`${HOST}customapi/Cart/Get`, {
            includeall,
        })
        .catch((error) => {
            console.log(error);
        });

    return data || {};
}

export async function fetchCartSummary() {
    const { data } = await axios.get(`${HOST}customapi/Cart/Summary`).catch((error) => {
        console.log(error);
    });
    return data || {};
}

export async function addCartPromoCode(code = '') {
    const { status } = await axios
        .put(`${HOST}customapi/Cart/PromoCode`, {
            code,
        })
        .catch((err) => err.response);

    return status;
}

export async function addToCart(items) {
    const { status } = await axios.post(`${HOST}customapi/Cart/Add`, items).catch((error) => {
        console.log(error);
    });

    if (status !== 200) {
        return {};
    }

    const [cartSummary, cart] = await Promise.all([fetchCartSummary(), fetchCart()]); // eslint-disable-line

    return {
        ...cartSummary,
        ...cart,
    };
}

const emptyCart = () =>
    axios.delete(`${HOST}customapi/Cart/EmptyCart?clearActive=true`).catch((err) => console.log('removeProduct - Error emptying cart: ', err)); // eslint-disable-line

export async function addProductToCart(product) {
    const { data } = await axios
        .post(`${HOST}customapi/Cart/AddProduct`, {
            product,
        })
        .catch(() => {});

    return data || {};
}

export const quickAddProductToCart = async (items, cartType = 'Regular') => {
    const { data } = await axios.post(`${HOST}customapi/Cart/AddProduct?cartType=${cartType}`, items).catch(() => {});
    return data || {};
};

export async function updateProductQty({ cartItemId, qty, note = '' }) {
    try {
        const res = await axios.put(`${HOST}customapi/Cart/UpdateQuantity`, {
            cartItemId,
            qty,
            note,
        });
        console.log(res.data);
        return res.data;
    } catch (error) {
        console.error(`Cart/UpdateQuantity Failed`, error.response);
        let errorMessage = 'An unknown error occurred';
        if (error.response && error.response.data && error.response.data.Message) {
            errorMessage = error.response.data.Message;
        } else if (error.message) {
            errorMessage = error.message;
        }

        swal({
            title: 'Maximum quantity exceeded',
            text: errorMessage,
            icon: 'error',
        }).then(() => {
            location.reload();
        });

        return {
            notify: true,
            error: errorMessage,
        };
    }
}

export async function removeProduct(id, caller = 'unknown') {
    if (!id || (Array.isArray(id) && id.length === 0)) {
        return;
    }

    const payload = {
        items: Array.isArray(id) ? id : [id],
        origin: '(removeProduct): ' + caller,
    };

    const { status } = await axios.delete(`${HOST}customapi/Cart/Delete`, { data: payload }).catch((err) => {
        console.log('removeProduct - Error deleting product: ', err);
        return { status: 500 }; // fallback for error cases
    });

    return status || 500;
}

export const getReviewOrder = async () => {
    const res = await axios.get(`${HOST}customapi/Cart/ReviewOrder`).catch((err) => err.response);

    if (res && res.status != 200) {
        var errorResponse = {
            status: res.status,
            Message: res.data.Message,
        };
    } else {
        res.data.Message = '';
        res.data.status = 200;
    }

    return (
        {
            ...(errorResponse || res.data),
            PromoCode: res.data.PromoCode || '',
            PONumber: res.data.PONumber,
        } || {}
    );
};

export async function checkPONumber(po) {
    const { data } = await axios.get(`${HOST}customapi/Cart/PONumberCheck?PONumber=${po}`).catch((err) => err.message);

    return data;
}

export async function placeOrder(poNumber = '') {
    const orderRes = await axios.post(`${HOST}customapi/Cart/PlaceOrder?poNumber=${poNumber}`).catch((error) => error.response);

    return {
        status: orderRes.status,
        orderNumber: orderRes.status === 200 ? orderRes.data : null,
        orderPlaced: orderRes.status === 200,
        isValidPONumber: orderRes.status === 200,
        Message: orderRes.data.Message || null,
    };
}

export const getCartInactive = async (cartType = 'Regular') => {
    const { data } = await axios
        .get(`${HOST}customapi/Cart/GetInactive`, {
            cartType,
        })
        .catch(() => {});
    return data || {};
};

export const removePartNote = (id) => axios.post(`${HOST}customapi/Cart/RemovePartNote?cartItemId=${id}`).catch((err) => err.response);

// ==================================================
// EDUCATION
// ==================================================

export async function getPage(page = 1, type) {
    const res = await axios.get(`${HOST}customapi/Education/GetPage?pageNumber=${page}`).catch((err) => err.response);

    if (type === 'IsRegistered' && res.data) {
        // return res.data.filter((k) => k.IsRegistered)
        return res.data;
    }

    return res.data
        ? {
              courses: res.data.filter((k) => k.Type === type),
          }
        : res;
}

export async function getCourse(id) {
    const res = await axios.get(`${HOST}customapi/Education/GetCourse?courseId=${id}`).catch((err) => err.response);

    return res.data
        ? {
              status: res.status,
              info: res.data,
          }
        : res;
}

export async function getSession(id) {
    const res = await axios.get(`${HOST}customapi/Education/GetSession?sessionId=${id}`).catch((err) => err.response);

    return res.data
        ? {
              status: res.status,
              info: res.data,
          }
        : res;
}

export async function addNewStudent(postData, sessionId) {
    const res = await axios.post(`${HOST}customapi/Education/AddNewStudent`, postData).catch((err) => err.response);

    if (res.status === 200 && typeof res.data === 'number') {
        const registerRes = await register({
            CourseSessionId: sessionId,
            StudentIds: [res.data],
        });

        if (registerRes.status === 200) {
            return {
                status: 200,
                message: registerRes.data[0].Message,
            };
        }
    }

    return res;
}

async function register(postData) {
    const res = await axios.post(`${HOST}customapi/Education/Register`, postData).catch((err) => err.response);

    return res;
}

export async function getRegisteredSessions() {
    const res = await axios.get(`${HOST}customapi/Education/GetRegisteredSessions`).catch((err) => err.response);
    return res;
}

export async function unregisterSession(id) {
    await axios.post(`${HOST}customapi/Education/CancelRegistration?RegistrationId=${id}`).catch((err) => err.response);
}

// ==================================================
// PRODUCTS
// ==================================================

export async function fetchProductAutocomplete(val, source) {
    if (val.length < 3) {
        return [];
    }

    const config = source
        ? {
              cancelToken: source.token,
          }
        : {};

    let products = [];

    const { data } = await axios
        .get(encodeURI(`${HOST}customapi/Product/Autocomplete?search=${val}`), config)
        .catch((thrown) => (axios.isCancel(thrown) ? 204 : []));

    products = data;

    if (products && !products.length) {
        products = await fetchProductSearch(val);
    }

    return products || 204;
}

export async function fetchProductSearch(val) {
    const { data } = await axios.get(encodeURI(`${HOST}customapi/Product/Search?search=${val}`)).catch(() => []);

    return data || [];
}

export const fetchProduct = async (id, qty = 1) => {
    const { data } = await axios.get(`${HOST}customapi/Product/Get/${id}?qty=${qty}`).catch((err) => err.response);
    return (
        {
            ...data.Product,
            qty: data.Quantity,
        } || {}
    );
};

export const fetchProductUpload = async (id, qty = 1) => {
    const { data } = await axios.get(`${HOST}customapi/Product/Get/${id}?qty=${qty}`).catch((err) => err.response);
    return (
        {
            ...data[0].Product,
            qty: data[0].Quantity,
            ActiveItemCount: data.ActiveItemCount,
            InActiveItemCount: data.InActiveItemCount,
        } || {}
    );
};
// ==================================================
// PUBLIC
// ==================================================

export async function contactUs(postData) {
    const { data, status } = await axios.post(`${HOST}customapi/Public/ContactUs`, formatFormData(postData)).catch((err) => err.response);

    return {
        data,
        status,
    };
}

// ==================================================
// UPLOADS
// ==================================================

export const deleteAttachment = async (id) => axios.delete(`${HOST}customapi/Uploads/DeleteAttachment?attachmentId=${id}`).catch((err) => err.response);

export async function uploadWarrantyImage({ id, description, image }) {
    const data = new FormData();
    data.append('file', image, image.name);

    const res = await axios
        .post(`${HOST}customapi/Uploads/Warranty?warrantyId=${id}&description=${description}`, data, {
            headers: {
                accept: 'application/json',
                'Accept-Language': 'en-US,en;q=0.8',
                'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
            },
        })
        .catch((err) => console.log(err.response)); // eslint-disable-line

    return res.data || {};
}

export async function postUploadOrder(file) {
    const fileData = new FormData();
    fileData.append('file', file, file.name);

    const { data, status, Message = '' } = await axios.post(`${HOST}customapi/Uploads/Order`, fileData).catch((err) => err.response);

    return {
        status,
        Message,
        data: {
            InvalidList: data.InvalidList || [],
            Results: data.Results ? data.Results.filter((k) => k.length < 2) : [],
            DidYouMean: data.Results ? data.Results.filter((k) => k.length > 1) : [],
            Message: data.Message || '',
            MaxItemCountMessage: data.MaxItemCountMessage || '',
        },
    };
}

// ==================================================
// WARRANTY
// ==================================================

export const addJob = async (warrantyId, modelId, modelJobId) =>
    axios.post(`${HOST}customapi/Warranty/AddJob?warrantyId=${warrantyId}&modelId=${modelId}&modelJobId=${modelJobId}`).catch((err) => err.response);

export const addLaborPart = async ({ warrantyId, productId, quantity }) =>
    axios.post(`${HOST}customapi/Warranty/AddLaborPart?warrantyId=${warrantyId}&productId=${productId}&quantity=${quantity}`).catch((err) => err.response);

export const removeJob = async ({ warrantyId, jobId }) =>
    axios.post(`${HOST}customapi/Warranty/RemoveJob?warrantyId=${warrantyId}&jobId=${jobId}`).catch((err) => err.response);

export async function removeAllJobs({ warrantyId, jobs }) {
    // eslint-disable-next-line
    const res = await Promise.all(
        jobs.map((k) =>
            removeJob({
                warrantyId,
                jobId: k,
            }),
        ),
    );
    const status = res.filter((k) => k.status !== 200).length ? 'error' : 200;

    if (status === 'error') {
        return {
            status,
        };
    }
    const { LaborTotal, PartsTotal, Parts, JobsPerformed } = await getWarranty(warrantyId);
    return {
        status,
        warranty: {
            LaborTotal,
            PartsTotal,
            data: Parts,
            JobsPerformed,
        },
    };
}
export const removeLaborPart = ({ warrantyId, partRecordId }) =>
    axios.post(`${HOST}customapi/Warranty/RemoveLaborPart?warrantyId=${warrantyId}&partRecordId=${partRecordId}`);

export async function removeAllLaborParts({ parts, warrantyId }) {
    // eslint-disable-next-line
    const res = await Promise.all(
        parts.map((k) =>
            removeLaborPart({
                warrantyId,
                partRecordId: k,
            }),
        ),
    );
    const status = res.filter((k) => k.status !== 200).length ? 'error' : 200;

    if (status === 'error') {
        return {
            status,
        };
    }

    const { LaborTotal, PartsTotal, Parts, JobsPerformed } = await getWarranty(warrantyId);

    return {
        status,
        warranty: {
            LaborTotal,
            PartsTotal,
            data: Parts,
            JobsPerformed,
        },
    };
}

export async function updateLaborPartQty({ warrantyId, partRecordId, newQuantity }) {
    const { data, status } = await axios
        .post(`${HOST}customapi/Warranty/UpdateLaborPartQuantity?warrantyId=${warrantyId}&partRecordId=${partRecordId}&newQuantity=${newQuantity}`)
        .catch((err) => err.response);

    return {
        data,
        status,
    };
}

export async function getSingleWarranty(id) {
    if (id !== 0) {
        // eslint-disable-next-line
        const [{ data }, { data: mf }, warranty] = await Promise.all([getOptionFields(), getEligibleManufacturers(), getWarranty(id)]);

        const mfCodes = {};
        mf.forEach((k) => (mfCodes[k.Id] = k.Code));

        return {
            warranty,
            mfCodes,
            statusCodes: cleanStatuses(data.Statuses),
        };
    }
    return {
        warranty: null,
    };
}

export async function getAndSetupWarranties(props = {}) {
    // eslint-disable-next-line
    const [{ data }, warranties, { data: mf }] = await Promise.all([getOptionFields(), getWarranties(props), getEligibleManufacturers()]);

    const statusCodes = data ? cleanStatuses(data.Statuses) : {};
    const mfCodes = {};
    mf.forEach((k) => (mfCodes[k.Code] = k.Id));

    return {
        statusCodes,
        warranties,
        mfCodes,
    };
}

export async function submitWarranty(war = {}) {
    if (!war.ReceiptAvailable || (typeof war.ReceiptAvailable === 'string' && !war.ReceiptAvailable.length)) {
        war.ReceiptAvailable = false;
    }

    const { data } = await axios.post(`${HOST}customapi/Warranty/Submit`, war).catch(() => {});
    return data || {};
}

export async function addWarrantyNote({ id = 0, note: Note = '', admin = false }) {
    const params = {
        Note,
    };

    if (admin) {
        params.username = 'abc';
    }

    return axios.post(`${HOST}customapi/Warranty/AddNote/${id}`, null, {
        params,
    });
}

export async function getWarrantyFields() {
    // eslint-disable-next-line
    const [{ data }] = await Promise.all([getOptionFields()]);

    return {
        ...data,
    };
}

export const getEligibleManufacturers = () => axios.get(`${HOST}customapi/Warranty/GetEligibleManufacturers`).catch(() => {});

const getOptionFields = () => axios.get(`${HOST}customapi/Warranty/GetOptionFields`).catch(() => {});

export async function getModels(id = 11, productCategory = '') {
    const { data } = await axios.get(`${HOST}customapi/Warranty/GetModels?manufacturerId=${id}&ProductCategory=${productCategory}`).catch(() => {});
    return data || {};
}

async function getWarranty(id) {
    const { data } = await axios.get(`${HOST}customapi/Warranty/Get/${id}`).catch(() => {});
    return data || {};
}

export async function getWarranties(props = {}) {
    let url = `${HOST}customapi/Warranty/GetWarranties?pageSize=10`;
    Object.keys(props).forEach((k) => (url += `&${k}=${props[k]}`));

    const res = await axios.get(url).catch(() => {});
    return res.data || {};
}

// ==================================================
// USER
// ==================================================

export async function postLogin({ valueOne, valueTwo }) {
    const res = await axios
        .post(`${HOST}customapi/User/Login`, {
            valueOne,
            valueTwo,
        })
        .catch((err) => {
            return err.response;
        });

    return res;
}

export async function forgotPassword(username) {
    const { data, status } = await axios.post(`${HOST}customapi/User/ForgotPassword?username=${username}`).catch((err) => err.response);

    return {
        data,
        status,
    };
}

export async function resetPassword({ oldPassword, newPassword }) {
    const { data, status } = await axios
        .post(`${HOST}customapi/User/ResetPassword`, {
            oldPassword,
            newPassword,
        })
        .catch((err) => err.response);

    return {
        data,
        status,
    };
}

export async function registerUser(postData) {
    if (!postData) {
        return;
    }

    const { status } = await axios.post(`${HOST}customapi/User/Register`, postData).catch((err) => err.response);

    return status;
}

export async function fetchCustomerInfo() {
    const res = await fetchCart();

    return res;
}

export async function getUserSummary() {
    const { status, data } = await axios.post(`${HOST}customapi/User/Summary`).catch((err) => err.response);

    if (status !== 200) {
        return console.log('error fetching user summary: ', data); // eslint-disable-line
    }

    return { status, data };
}

export async function getCurrentUser() {
    const { status, data } = await axios.get(`${HOST}customapi/User/GetCurrent`).catch((err) => err.response);

    return {
        status,
        data,
    };
}

// ==================================================
// ORDER
// ==================================================

export const getOrderDetail = (orderNumber) =>
    axios.get(`${HOST}customapi/Order/Detail?orderNum=${orderNumber}`).catch((err) => console.log('res error: ', err.response)); // eslint-disable-line

export const getOrderConfirmation = (orderNumber) =>
    axios.get(`${HOST}customapi/Order/Confirmation?orderNum=${orderNumber}`).catch((err) => console.log('res error: ', err.response)); // eslint-disable-line

export const postStatementRequest = ({ year = '', month = '', dest = '', emailFax = '' }) =>
    axios.post(`${HOST}customapi/Order/StatementRequest?year=${year}&month=${month}&dest=${dest}&emailFax=${emailFax}`).catch((err) => err.response);

export const getStatementList = async () => {
    const { data, status } = await axios.get(`${HOST}customapi/Order/HistoricalStatement`).catch((err) => err.response);

    return {
        data,
        status,
    };
};

export async function getOrders(postData = {}) {
    Object.keys(postData).forEach((k) => {
        if (!postData[k]) {
            delete postData[k];
        }
    });

    const { data, status } = await axios
        .get(`${HOST}customapi/Order/Search`, {
            params: postData,
        })
        .catch((err) => err.response);

    return {
        data,
        status,
    };
}

export async function getBackOrders() {
    const { data, status } = await axios.get(`${HOST}customapi/Order/Backorder`).catch((err) => err.response);

    return {
        data: {
            // Orders: data.Backorders
            // Orders: data.backorderLines,
            Orders: data.Backorders.backorderLines,
            ItemLocationInventory: data.ItemLocationInventory,
            IsAlternateWarehouseDisabled: data.IsAlternateWarehouseDisabled,
        },
        status,
    };
}

export async function backOrdersCancellationRequest(backOrderCancellationObjects) {
    const { status, data } = await axios
        .post(`${HOST}customapi/Order/BackOrdersCancellationRequest`, backOrderCancellationObjects)
        .catch((err) => err.response);

    return { status, data };
}

export const getStatementPaymentList = async () => {
    const { data, status } = await axios.get(`${HOST}customapi/Payment/GetStatementData`).catch((err) => err.response);

    return {
        data: data.Result,
        errorMessage: data.ErrorMessage,
        pendingRecords: data.PendingRecords,
        pendingRecordsMessage: data.PendingRecordsMessage,
        status,
    };
};

export const getPaymentInstrumentlist = async () => {
    const data = await axios.get(`${HOST}customapi/Payment/GetPaymentInstrument`).catch((err) => err.response);

    return data;
};

export async function paymentRequest(paymentRequestObject) {
    const { status, data } = await axios.post(`${HOST}customapi/Payment/PaymentRequest`, paymentRequestObject).catch((err) => err.response);

    return { status, data };
}

export async function UpdateCustomer(CustomerRequestObject) {
    const { status, data } = await axios.post(`${HOST}customapi/User/UpdateCustomer`, CustomerRequestObject).catch((err) => err.response);

    return { status, data };
}

export async function sendInvoice({ invoiceNumber, isEmail, emailFax }) {
    const res = await axios
        .post(`${HOST}customapi/Order/SendInvoice?invoiceNumber=${invoiceNumber}&isEmail=${isEmail}&emailFax=${emailFax}`)
        .catch((err) => err.response);

    return res.data;
}

export const getFortisOnboardingRequestUrl = async () => {
    const { data, status } = await axios.get(`${HOST}customapi/Payment/GetFortisOnboardingRequestUrl`).catch((err) => err.response);

    return {
        data,
        status,
    };
};

// ==================================================
// MISC
// ==================================================

export async function getWarehouse() {
    const { status, data } = await axios.get(`${HOST}customapi/Cart/Get`).catch((err) => err.response);

    return status === 200 ? getWarehouseTimezoneOffSet(data.User.NetSuiteCustomer.ShippingWarehouse) : 'error';
}

// ==================================================
// User
// ==================================================

export const enableLoginAsDealer = async (username) => {
    const { data } = await axios.post(`${HOST}customapi/User/EnableLoginAsDealer?username=${username}`).catch((ex) => {
        return 'Not Found';
    });
    return { data: data || 'Not Found' } || {};
};

export const getLoginAsDealer = async () => {
    const { data } = await axios.get(`${HOST}customapi/User/GetLoginAsDealer`).catch(() => {});
    return { data } || {};
};

export const disableLoginAsDealer = async () => {
    const { data } = await axios.post(`${HOST}customapi/User/DisableLoginAsDealer`).catch(() => {});
    return { data } || {};
};

export const PortalUserToken = async () => {
    const { data } = await axios.get(`${HOST}customapi/PartSmart/GetPortalUserToken`).catch(() => {});
    return { data } || {};
};

export const loadCustomerEmail = async () => {
    const { data } = await axios.get(`${HOST}customapi/PriceFileRequest/GetCustomerEmail`).catch(() => {});
    return { data } || {};
};

export async function PriceFileRequest(postData) {
    if (!postData) {
        return;
    }
    const { status } = await axios.post(`${HOST}customapi/PriceFileRequest/PriceFileRequest`, postData).catch((err) => err.response);

    return status;
}

////////// Fortis / Payments //////////

function loadScriptFile(url, onLoadCallback) {
    // 1. Create a new <script> element
    const script = document.createElement('script');

    // 2. Set the src attribute to the script file's URL
    script.src = url;

    // Optional: Add an event listener to know when the script has loaded
    script.onload = () => {
        console.log(`${url} has been loaded successfully.`);

        if (onLoadCallback) {
            onLoadCallback();
        }
    };

    // Optional: Add an event listener to handle errors
    script.onerror = () => {
        console.error(`Error loading ${url}.`);
    };

    // 3. Append the script element to the <head> of the document
    // Appending to the <body>, especially near the end, also works well
    document.head.appendChild(script);
}

export async function getPaymentInstruments() {
    const { data } = await axios.get(`${HOST}customapi/Cart/GetPaymentInstruments`).catch((err) => {
        console.error('getPaymentInstruments Error:', err);
    });

    return data || [];
}

export async function setDefaultFortisPaymentToken(paymentCard) {
    const postedBody = {
        id: paymentCard.id,
    };

    try {
        const response = await axios.post(`${HOST}customapi/Payment/SetDefaultFortisPaymentToken`, postedBody);

        // console.log('setDefaultFortisPaymentToken response:');
        // console.log(response);

        return true;
    } catch (error) {
        let message = error.message || 'An error occurred while setting your default card. Please try again.';

        if (error.response && error.response.data && error.response.data.Message) {
            message = error.response.data.Message;
        }

        swal({
            title: 'Error setting default card',
            text: message,
            icon: 'error',
        });
    }

    return false;
}

export async function deletePaymentToken(paymentCard) {
    const postedBody = {
        id: paymentCard.id,
    };

    try {
        const response = await axios.post(`${HOST}customapi/Payment/DeleteFortisPaymentToken`, postedBody);

        // console.log('deletePaymentToken response:');
        // console.log(response);

        return true;
    } catch (error) {
        let message = error.message || 'An error occurred while deleting your card. Please try again.';

        if (error.response && error.response.data && error.response.data.Message) {
            message = error.response.data.Message;
        }

        swal({
            title: 'Error deleting card',
            text: message,
            icon: 'error',
        });
    }

    return false;
}

export async function initFortisElements(elementSelector, shippingRes, onDone) {
    if (!shippingRes.FortisClientToken || shippingRes.FortisClientToken === '') {
        console.warn('Fortis client token not found, skipping Fortis Elements initialization.');
        return;
    }

    if (isProd && isProdDomain) {
        loadScriptFile('https://js.fortis.tech/commercejs-v1.0.0.min.js', () => {
            loadFortisElements(elementSelector, shippingRes, onDone);
        });
    } else {
        loadScriptFile('https://js.sandbox.fortis.tech/commercejs-v1.0.0.min.js', () => {
            loadFortisElements(elementSelector, shippingRes, onDone);
        });
    }
}

export async function loadFortisElements(elementSelector, shippingRes, onDone) {
    const containerElement = await waitForElm('#fortis-card-element-container');
    const elementDiv = await waitForElm(elementSelector);

    const elements = new Commerce.elements(shippingRes.FortisClientToken);

    // Element rendered and ready
    elements.on('ready', function (event) {
        // console.log('loadFortisElements -> ready:');
        // console.log(event);

        if (containerElement != null) {
            // Remove none class from element container to make it visible
            containerElement.classList.remove('none');
        }
    });

    // Event for Save Card / Tokenization
    elements.on('done', async function (event) {
        // console.log('loadFortisElements -> done:');
        // console.log(event);

        try {
            const postedBody = {
                Data: event.data,
                ShoppingCartId: shippingRes.ShoppingCartId,
            };

            const { data } = await axios.post(`${HOST}customapi/Payment/PostFortisTokenization`, postedBody);

            // Pass data back to the caller (e.g. to update payment instruments list, etc.)
            onDone(data);
        } catch (error) {
            console.log('PostFortisTokenization Error:');
            console.log(error);

            swal({
                title: 'Fortis',
                text: error.message || 'An error occurred while attempting to load the credit card form.',
                icon: 'error',
            });
        }
    });

    elements.on('error', (err) => {
        console.error('initFortisElements -> error:', err);

        let errorMessage = 'An error occurred while saving your card. Please try again.';

        if (err.data && err.data && err.data.detail) {
            errorMessage = err.data.detail;
        } else if (err.message) {
            errorMessage = err.message;
        }

        swal({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
        });
    });

    const environment = isProd && isProdDomain ? 'production' : 'sandbox';

    // console.log('loadFortisElements -> environment:');
    // console.log(environment);

    elements.create({
        container: elementDiv,
        environment: environment,
        view: 'card-single-field',
        defaultCountry: 'US',
        floatingLabels: false,
        showReceipt: false,
        fields: {
            billing: false,
        },
    });
}

const booleanToTF = (value) => (value === 'true' || value === true ? 'T' : 'F');
const TFtoBoolean = (value) => (value === 'T' ? true : false);

export async function getDealerAgreement() {
    const { data } = await axios.get(`${HOST}customapi/DealerAgreement/Get`).catch((err) => {
        console.error('getDealerAgreement Error:', err);
    });

    if (data != null) {
        const patchedData = {
            ...data,
            bsCommercialEngines: TFtoBoolean(data.bsCommercialEngines),
            bsResidentialEngines: TFtoBoolean(data.bsResidentialEngines),
            bsIncludeInDealerLocator: TFtoBoolean(data.bsIncludeInDealerLocator),
            bsServiceBrandNotSold: TFtoBoolean(data.bsServiceBrandNotSold),
        };

        return patchedData;
    }

    return data || {};
}

export async function updateDealerAgreement(contract, pdf) {
    const postedBody = {
        bsCommercialEngines: booleanToTF(contract.bsCommercialEngines),
        bsResidentialEngines: booleanToTF(contract.bsResidentialEngines),
        bsIncludeInDealerLocator: booleanToTF(contract.bsIncludeInDealerLocator),
        bsServiceBrandNotSold: booleanToTF(contract.bsServiceBrandNotSold),
        pdfFile: pdf,
    };

    try {
        const response = await axios.post(`${HOST}customapi/DealerAgreement/Post`, postedBody);

        return true;
    } catch (error) {
        console.log('updateDealerAgreement Error:');
        console.log(error);

        swal({
            title: 'Briggs Dealer Agreement',
            text: error.message || 'An error occurred while attempting to save the dealer agreement.',
            icon: 'error',
        });
    }

    return false;
}
